
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ChatInterface, ChatReviewInterface,
  ProfileMapInterface,
} from '@/services/QC-V2/chat/interfaces';
import { ScoreTypeInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface } from '@/services/QC-V2/score-grade/interfaces';
import { TagInterface } from '@/services/QC-V2/tag/interfaces';
import DossierFeedbackRepositoryInterface from '@/services/Dossier/feedback/interfaces';
import FeedbackDialog, { FeedbackDialogDataInterface } from '@/components/dialogs/qc-v2/feedback/FeedbackDialog.vue';
import {
  ChatFeedbackCreateInterface,
  FeedbackRepositoryInterface,
  FeedbackStatus,
} from '@/services/QC-V2/feedback/interfaces';
import ChatEscalateDialog from '@/components/dialogs/qc-v2/escalation/ChatEscalateDialog.vue';
import { ChatEscalationCreateData, EscalationRepositoryInterface } from '@/services/QC-V2/escalation/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';


@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'dossierFeedbackRepository',
    'feedbackRepositoryV2',
    'escalationRepositoryV2',
    'userRepository',
  ],
  components: {
    FeedbackDialog,
    ChatEscalateDialog,
  },
})
export default class ChatReviewsDetails extends Vue {
  @Prop() private chat!: ChatInterface;
  @Prop() private scoreTypes!: ScoreTypeInterface[];
  @Prop() private scoreGrades!: ScoreGradeInterface[];
  @Prop() private tags!: TagInterface[];
  @Prop() private profilesMap!: ProfileMapInterface;

  private dossierFeedbackRepository!: DossierFeedbackRepositoryInterface;
  private feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private escalationRepositoryV2!: EscalationRepositoryInterface;
  private userRepository!: UserRepoInterface;

  private scoreTypesMap: { [key: number]: string } = {};
  private scoreGradesMap: { [key: number]: string } = {};
  private tagsMap: { [key: number]: TagInterface } = {};

  private collapsed: boolean = false;

  private dataForFeedbackDialog: FeedbackDialogDataInterface | undefined = undefined;
  private feedbackDialogVisible: boolean = false;

  private escalateDialogVisible: boolean = false;

  private users: UserBaseInfoInterface[] = [];

  private async created(): Promise<void> {
    this.createScoresMap();
    this.createTagsMap();
    await this.fetchUsers();
  }

  @Watch('chat', { deep: true })
  private async handleChatUpdate(): Promise<void> {
    await this.fetchUsers();
  }

  private createScoresMap(): void {
    this.scoreTypes.forEach((scoreType) => {
      this.scoreTypesMap[scoreType.id] = scoreType.label;
    });

    this.scoreGrades.forEach((scoreGrade) => {
      this.scoreGradesMap[scoreGrade.id] = scoreGrade.label;
    });
  }

  private createTagsMap(): void {
    this.tags.forEach((tag) => {
      this.tagsMap[tag.id] = tag;
    });
  }

  private async sendToDossier(chatId: string, review: ChatReviewInterface, commentId: number): Promise<void> {
    try {
      await this.dossierFeedbackRepository.createChatComment({
        chatId,
        commentId,
        reviewId: review.id,
        to: review.reviewedId,
        from: this.$store.getters.id,
      });
      this.$successDialog.show('Comment successfully send to dossier');
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private openFeedbackDialog(reviewIndex: number): void {
    const review = this.chat.reviews[reviewIndex];

    const scores: string[] = [];

    review.scores.forEach((score) => {
      const type = this.scoreTypes.find((st) => st.id === score.scoreTypeId);
      const grade = this.scoreGrades.find((sg) => sg.id === score.scoreGradeId);

      if (!type || !grade) {
        return;
      }

      scores.push(`${type.label} - ${grade.label}`);
    });

    const tags: TagInterface[] = [];
    review.tags.forEach((rt) => {
      const tag = this.tags.find((t) => t.id === rt.tagId);
      if (!tag) {
        return;
      }

      tags.push(tag);
    });

    this.dataForFeedbackDialog = {
      user: {
        id: review.reviewedId,
        name: `${this.profilesMap[review.reviewedId].firstName} ${this.profilesMap[review.reviewedId].lastName}`,
      },
      comments: review.comments,
      scores,
      tags,
      usersMap: this.profilesMap,
      reviewIndex,
    };

    this.feedbackDialogVisible = true;
  }

  private async createFeedback(reviewIndex?: number): Promise<void> {
    if (!reviewIndex && reviewIndex !== 0) {
      this.$errorDialog.show('Missing review for feedback!');
      return;
    }

    const review = this.chat.reviews[reviewIndex];
    if (!review) {
      this.$errorDialog.show('Missing review for feedback!');
      return;
    }

    const feedbackData: ChatFeedbackCreateInterface = {
      isDraft: false,
      chatId: this.chat.id,
      feedbackTo: review.reviewedId,
      feedbackFrom: this.$store.getters.id,
      status: FeedbackStatus.SENT,
    };

    const scores: { [key: number]: number } = {};
    review.scores.forEach((score) => {
      scores[score.scoreTypeId] = score.scoreGradeId;
    });

    const tags: TagInterface[] = [];
    review.tags.forEach((rt) => {
      const tag = this.tags.find((t) => t.id === rt.tagId);
      if (!tag) {
        return;
      }

      tags.push(tag);
    });

    try {
      await this.feedbackRepositoryV2.createChatFeedback(
        feedbackData,
        {
          chatId: this.chat.id,
          reviewedId: review.reviewedId,
          reviewerId: this.$store.getters.id,
          comments: review.comments,
          isDraft: false,
          scores,
          tags,
        },
      );
      this.chat.feedbacks.push({
        id: 0,
        feedbackTo: feedbackData.feedbackTo,
        feedbackFrom: feedbackData.feedbackFrom,
        status: FeedbackStatus.SENT,
        timestamp: new Date(),
      });
      this.$successDialog.show('Feedback created Successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private openEscalationDialog(): void {
    this.escalateDialogVisible = true;
  }

  private async createEscalation(data: ChatEscalationCreateData): Promise<void> {
    if (this.chat.escalation) {
      this.$successDialog.show('Chat already escalated');
      return;
    }
    try {

      data.agentsIds = this.users.map((user) => user.id);
      await this.escalationRepositoryV2.createChatEscalation(data);
      this.$emit('chat-reviewed', this.chat.id);
      this.chat.escalation = {
        chat: this.chat,
        escalatedFrom: data.escalatedFrom,
        departmentManager: data.departmentManager || '',
        shiftManager: data.shiftManager || '',
        reason: data.reason,
        status: data.status,
        id: 0,
        priority: data.priority,
        timestamp: new Date().toISOString(),
      };
      this.$successDialog.show('Escalation created Successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async fetchUsers(): Promise<void> {
    const emails = this.chat.agents.map((agent) => agent.email);
    this.users = await this.userRepository.getUsersByEmails(emails);
  }
}
